<script lang="ts" setup>
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { i18n } from '@/i18n';
import { useForecastForReceiptListSelectInputStore } from './useForecastForReceiptListSelectInputStore';
import { ForecastForReceiptFormState } from '../domains/entities/ForecastForReceiptMenu';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';

const props = withDefaults(
  defineProps<{
    selectInputIndex: number;
    storeTag: string;
    selectedForecastForReceiptIdProp?: string;
    selectedForecastForReceiptFormState?: ForecastForReceiptFormState;
    showLabel?: boolean;
    useReset?: boolean;
    reload?: boolean;
    useTeleport?: boolean;
  }>(),
  {
    selectedForecastForReceiptIdProp: undefined,
    selectedForecastForReceiptFormState: undefined,
    useReset: true,
  }
);

const emits = defineEmits<{
  (e: 'selectedForecastForReceipt', forecastForReceiptId?: string): void;
  (e: 'selectedForecastForReceiptFormState', forecastForReceiptFormState?: ForecastForReceiptFormState): void;
}>();

const t = i18n.t;

const store = useForecastForReceiptListSelectInputStore(props.storeTag)();
const { state } = storeToRefs(store);

onMounted(() => {
  store.init();
  if (props.selectedForecastForReceiptIdProp) {
    state.value.selectedForecastForReceiptId = props.selectedForecastForReceiptIdProp;
  }
});

watch(
  () => props.selectedForecastForReceiptIdProp,
  (newVal) => {
    state.value.selectedForecastForReceiptId = newVal;
  }
);

watch(
  () => props.reload,
  async (newVal) => {
    if (newVal) {
      await store.reloadForecastForReceiptData();
    }
  }
);

/**
 * 受領用内示名を選択した際の処理
 * @param index - 選択した受領用内示のインデックス
 */
const onSelectedForecastForReceipt = (index: number) => {
  store.onSelectedForecastForReceipt(index);
  emits('selectedForecastForReceipt', state.value.selectedForecastForReceiptId);
  emits('selectedForecastForReceiptFormState', state.value.selectedForecastForReceiptFormState);
};

/**
 * 外すボタンを押した際の処理
 * @param value - 選択した受領用内示
 */
const onUpdatedValue = (value: string) => {
  if (value === '') {
    state.value.selectedForecastForReceiptId = undefined;
    emits('selectedForecastForReceipt', undefined);
    emits('selectedForecastForReceiptFormState', undefined);
  }
};

/**
 * セレクトボックスをクリックした際の処理
 */
const onClickSelectInput = async () => {
  await store.reloadForecastForReceiptData();
  state.value.selectInputState = true;
};

const useLabel = computed(() => (props.showLabel ? t('受領用内示') : ''));

const selectInputOptions = computed(() => {
  return [t(UNASSIGNED_COLUMN), ...store.ForecastForReceiptListNames];
});
</script>

<template>
  <SelectInput
    :index="selectInputIndex"
    :model-value="store.selectedForecastForReceipt"
    :placeholder="t('受領用内示名')"
    :label="useLabel"
    :filter-type="'FORECAST_FOR_RECEIPT'"
    :use-reset="useReset"
    :is-open-prop="state.selectInputState"
    :prop-options="selectInputOptions"
    :prop-paging-state="state.pagingState"
    :prop-paging-options="state.pagingOptions"
    :use-teleport="useTeleport"
    @click:select-input="() => onClickSelectInput()"
    @close="() => (state.selectInputState = false)"
    @update:model-value="(value) => onUpdatedValue(value)"
    @update:value-with-index="(_, index) => onSelectedForecastForReceipt(index)"
  />
</template>
